import { DeleteOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Col,
  Descriptions,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCancelAllJobs, useCancelJob, useCancelWaitingJobs, useConsumers, useDeleteJob, useDeleteJobs, useJob, useJobs, useRaceChildren, useWorkflowChildren } from '../api/hooks';
import type { JobAttempt, JobListItem, JobStatus, RaceChild, WorkflowChildJob } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { JobStatusTag } from '../utils/status';
import { formatDateTime, formatDuration, toJsonText } from '../utils/format';
import { extractMedia } from '../utils/media';
import { MediaResult } from '../components/MediaResult';

const { Title, Text, Paragraph } = Typography;

const STATUS_FILTER = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'queued', label: 'Đang chờ' },
  { value: 'assigned', label: 'Đã giao worker' },
  { value: 'processing', label: 'Đang xử lý' },
  { value: 'awaiting_callback', label: 'Chờ callback' },
  { value: 'completed', label: 'Hoàn tất' },
  { value: 'failed', label: 'Thất bại' },
  { value: 'cancelled', label: 'Đã hủy' },
];

const TYPE_FILTER = [
  { value: '', label: 'Tất cả loại' },
  { value: 'image', label: 'Hình ảnh' },
  { value: 'video', label: 'Video' },
  { value: 'text', label: 'Text' },
];

// Workflow: mỗi bước (node model/nhóm) là 1 JOB CON. Mặc định ẩn để 1 lượt chạy không bị
// "nhân đôi/ba" theo số bước; bật "Hiện bước workflow" khi cần soi lỗi từng bước ngay ở đây.
const WORKFLOW_STEP_FILTER = [
  { value: 'hide', label: 'Ẩn bước workflow' },
  { value: 'show', label: 'Hiện bước workflow' },
  { value: 'only', label: 'Chỉ bước workflow' },
];

/** Gom mọi URL media của 1 output/input (để loại input "echo" khỏi cột Kết quả). */
function mediaUrls(value: unknown): string[] {
  const m = extractMedia(value);
  return [...m.images, ...m.videos, ...m.audios];
}

const ACTIVE_STATES = ['queued', 'assigned', 'processing', 'awaiting_callback'];

export default function JobsPage() {
  const { message } = AntApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [type, setType] = useState(() => searchParams.get('type') ?? '');
  const [consumerId, setConsumerId] = useState(() => searchParams.get('consumerId') ?? '');
  // Lựa chọn được GHI NHỚ (localStorage): bật 1 lần rồi F5 vẫn còn, không phải chọn lại mỗi lần.
  const [workflowSteps, setWorkflowSteps] = useState(
    () => searchParams.get('workflowSteps') ?? localStorage.getItem('gw.jobs.workflowSteps') ?? 'hide',
  );
  const [page, setPage] = useState(1);
  const pageSize = 20; // 20 kết quả / trang
  const list = useJobs(status, type, consumerId, page, pageSize, workflowSteps);
  const consumers = useConsumers();
  const cancel = useCancelJob();
  const cancelAll = useCancelAllJobs();
  const cancelWaiting = useCancelWaitingJobs();
  const delJob = useDeleteJob();
  const delJobs = useDeleteJobs();

  // Cập nhật bộ lọc (status/type/consumerId) + đồng bộ URL + reset về trang 1.
  const updateFilter = (
    patch: Partial<{ status: string; type: string; consumerId: string; workflowSteps: string }>,
  ) => {
    const next = { status, type, consumerId, workflowSteps, ...patch };
    setStatus(next.status);
    setType(next.type);
    setConsumerId(next.consumerId);
    setWorkflowSteps(next.workflowSteps);
    if (patch.workflowSteps !== undefined) localStorage.setItem('gw.jobs.workflowSteps', next.workflowSteps);
    setPage(1);
    const sp: Record<string, string> = {};
    if (next.status) sp.status = next.status;
    if (next.type) sp.type = next.type;
    if (next.consumerId) sp.consumerId = next.consumerId;
    if (next.workflowSteps && next.workflowSteps !== 'hide') sp.workflowSteps = next.workflowSteps;
    setSearchParams(sp, { replace: true });
  };

  const consumerOptions = [
    { value: '', label: 'Tất cả khóa API' },
    ...(consumers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const onCancel = async (id: string) => {
    try {
      await cancel.mutateAsync(id);
      message.success('Đã gửi yêu cầu hủy');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await delJob.mutateAsync(id);
      setSelectedKeys((ks) => ks.filter((k) => k !== id));
      message.success('Đã xóa lịch sử');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onCancelAll = async () => {
    try {
      const r = await cancelAll.mutateAsync();
      message.success(`Đã dừng ${r.count} job`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onCancelWaiting = async () => {
    try {
      const r = await cancelWaiting.mutateAsync();
      message.success(`Đã dừng ${r.count} job đang chờ`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDeleteSelected = async () => {
    try {
      const r = await delJobs.mutateAsync(selectedKeys as string[]);
      setSelectedKeys([]);
      message.success(`Đã xóa ${r.count} lịch sử`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    {
      title: 'Mã',
      dataIndex: 'id',
      key: 'id',
      width: 92,
      render: (v: string) => <Text code>{v?.slice(0, 8)}</Text>,
    },
    {
      title: 'Năng lực / Nhóm / Model',
      key: 'capability',
      // Đặt bề rộng cố định + cấm bẻ giữa từ: tên dài (vd "Omni-flash-no-watermark")
      // từng bị ép xuống dòng theo từng ký tự vì cột quá hẹp.
      width: 170,
      render: (_: unknown, r: JobListItem) => {
        const target = r.model?.name ?? r.modelGroup?.name ?? r.capability?.name ?? null;
        const noBreak: React.CSSProperties = { wordBreak: 'keep-all', overflowWrap: 'anywhere' };
        // Workflow: job cha hiện tên workflow; job con (1 bước) hiện thêm tag "Bước" + model
        // thật đã chạy — phân biệt được ngay khi bật "Hiện bước workflow".
        if (r.workflowName) {
          const isStep = !!r.workflowParentJobId;
          return (
            <Space direction="vertical" size={0} style={noBreak}>
              <span>
                <Tag color={isStep ? 'geekblue' : 'purple'}>{isStep ? 'Bước' : 'Workflow'}</Tag>
                <Text>{r.workflowName}</Text>
              </span>
              {isStep && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {target ? target : `bước "${r.workflowNodeId || '?'}"`}
                </Text>
              )}
            </Space>
          );
        }
        return <span style={noBreak}>{target ?? '—'}</span>;
      },
    },
    {
      // Model con thực sự đã tạo ra kết quả (khi gọi qua nhóm); trống nếu không qua nhóm.
      title: 'Model con',
      key: 'groupChildModel',
      // Rộng hơn hẳn để tên dài (vd "GPT Playwright · Auto_gpt2 (Trung gian & Fallback)")
      // không bị bẻ xuống dòng dài dằng dặc; cấm bẻ giữa từ như cột Nhóm.
      width: 240,
      render: (_: unknown, r: JobListItem) => (
        <Space size={4} style={{ wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
          {r.groupChildModelName && <Text>{r.groupChildModelName}</Text>}
          {r.raceChildCount ? (
            <Tooltip title={`Đã tạo đồng thời ${r.raceChildCount} model con (Race)`}>
              <Tag color="purple">🏁×{r.raceChildCount}</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Kết quả',
      key: 'result',
      width: 130,
      render: (_: unknown, r: JobListItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MediaResult output={r.output} compact excludeUrls={r.inputUrls} />
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: JobListItem['status']) => <JobStatusTag status={s} />,
    },
    {
      title: 'Lỗi',
      dataIndex: 'errorClass',
      key: 'errorClass',
      render: (v: string) => v || '—',
    },
    {
      // Số lần model con lỗi (failover). Để trống khi lần đầu thành công; hiện số khi có lỗi.
      title: 'Số lần lỗi',
      dataIndex: 'errorAttempts',
      key: 'errorAttempts',
      width: 90,
      align: 'center' as const,
      render: (v: number) => (v && v > 0 ? <Tag color="orange">{v}</Tag> : null),
    },
    {
      title: 'Worker',
      key: 'worker',
      render: (_: unknown, r: JobListItem) =>
        r.claimedByWorkerName ? (
          <Text>{r.claimedByWorkerName}</Text>
        ) : r.claimedByWorkerId ? (
          <Text code>{r.claimedByWorkerId.slice(0, 8)}</Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Khóa API',
      key: 'consumer',
      render: (_: unknown, r: JobListItem) =>
        // Có tên khóa = lịch sử từ phần mềm thứ 3; trống = phát từ gateway.
        r.consumerName ? <Text>{r.consumerName}</Text> : null,
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      // Tổng thời lượng từ lúc tạo đến lúc hoàn tất/lỗi (vd 30s, 1p30s). Chưa xong -> "—".
      title: 'Thời lượng tạo',
      key: 'duration',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, r: JobListItem) => <Text>{formatDuration(r.createdAt, r.completedAt)}</Text>,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 140,
      render: (_: unknown, r: JobListItem) => (
        <Space onClick={(e) => e.stopPropagation()}>
          {ACTIVE_STATES.includes(r.status) && (
            <Popconfirm
              title="Hủy công việc này?"
              okText="Hủy việc"
              cancelText="Đóng"
              onConfirm={() => onCancel(r.id)}
            >
              <Button size="small" danger icon={<StopOutlined />}>
                Hủy
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Xóa lịch sử này?"
            okText="Xóa"
            cancelText="Đóng"
            onConfirm={() => onDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} title="Xóa" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Lịch sử
        </Title>
        <Space>
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`Xóa ${selectedKeys.length} lịch sử đã chọn?`}
              okText="Xóa"
              cancelText="Đóng"
              onConfirm={onDeleteSelected}
            >
              <Button danger icon={<DeleteOutlined />} loading={delJobs.isPending}>
                Xóa đã chọn ({selectedKeys.length})
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Dừng toàn bộ job (đang chờ + đang xử lý + trì hoãn)?"
            okText="Dừng tất cả"
            cancelText="Đóng"
            onConfirm={onCancelAll}
          >
            <Button danger icon={<StopOutlined />} loading={cancelAll.isPending}>
              Dừng toàn bộ job
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Dừng toàn bộ job đang chờ?"
            okText="Dừng"
            cancelText="Đóng"
            onConfirm={onCancelWaiting}
          >
            <Button danger icon={<StopOutlined />} loading={cancelWaiting.isPending}>
              Dừng job Đang chờ
            </Button>
          </Popconfirm>
          <Select
            value={workflowSteps}
            style={{ width: 180 }}
            options={WORKFLOW_STEP_FILTER}
            onChange={(v) => updateFilter({ workflowSteps: v })}
          />
          <Select
            value={type}
            style={{ width: 150 }}
            options={TYPE_FILTER}
            onChange={(v) => updateFilter({ type: v })}
          />
          <Select
            value={consumerId}
            style={{ width: 180 }}
            options={consumerOptions}
            loading={consumers.isLoading}
            onChange={(v) => updateFilter({ consumerId: v })}
          />
          <Select
            value={status}
            style={{ width: 180 }}
            options={STATUS_FILTER}
            onChange={(v) => updateFilter({ status: v })}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()}>
            Làm mới
          </Button>
        </Space>
      </Space>

      {/* size="small" + .gw-jobs-table (theme.css): thu gọn bảng như zoom ~90% cho dễ nhìn nhiều cột. */}
      <Table<JobListItem>
        rowKey="id"
        size="small"
        className="gw-jobs-table"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        columns={columns}
        scroll={{ x: 1100 }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          preserveSelectedRowKeys: true,
        }}
        onRow={(r) => ({
          onClick: () => setSelectedId(r.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: list.data?.total ?? 0,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />

      <JobDetailDrawer
        id={selectedId}
        onClose={() => setSelectedId(undefined)}
        onCancel={onCancel}
        canceling={cancel.isPending}
      />
    </div>
  );
}

function JobDetailDrawer({
  id,
  onClose,
  onCancel,
  canceling,
}: {
  id?: string;
  onClose: () => void;
  onCancel: (id: string) => void;
  canceling: boolean;
}) {
  const { data, isLoading } = useJob(id);

  return (
    <Modal
      title="Chi tiết lịch sử"
      open={!!id}
      onCancel={onClose}
      destroyOnClose
      centered
      width="96vw"
      style={{ maxWidth: 1600, top: 0, paddingBottom: 0 }}
      styles={{ body: { maxHeight: '84vh', overflow: 'auto' } }}
      footer={
        data && ACTIVE_STATES.includes(data.status) ? (
          <Popconfirm
            title="Hủy công việc này?"
            okText="Hủy việc"
            cancelText="Đóng"
            onConfirm={() => data && onCancel(data.id)}
          >
            <Button danger icon={<StopOutlined />} loading={canceling}>
              Hủy công việc
            </Button>
          </Popconfirm>
        ) : null
      }
    >
      {isLoading && <Text>Đang tải...</Text>}
      {data && (
        <Row gutter={16}>
          {/* Cột trái: thông tin chung + Đầu vào */}
          <Col xs={24} lg={8} style={detailColStyle}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Mã">{data.id}</Descriptions.Item>
              <Descriptions.Item label="Năng lực / Nhóm / Model">
                {data._workflow?.name ? (
                  <Space size={4}>
                    <Tag color="purple">Workflow</Tag>
                    {data._workflow.name}
                  </Space>
                ) : data.workflowParent ? (
                  <Space size={4} direction="vertical">
                    <Space size={4}>
                      <Tag color="purple">Workflow</Tag>
                      {data.workflowParent.name ?? '—'}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      bước "{data.workflowParent.nodeId || '?'}"
                      {data.model?.name || data.modelGroup?.name ? ` · ${data.model?.name ?? data.modelGroup?.name}` : ''}
                    </Text>
                  </Space>
                ) : (
                  data.model?.name ?? data.modelGroup?.name ?? data.capability?.name ?? '—'
                )}
              </Descriptions.Item>
              {data.groupChildModelName && (
                <Descriptions.Item label="Model con">{data.groupChildModelName}</Descriptions.Item>
              )}
              {data.errorAttempts ? (
                <Descriptions.Item label="Số lần lỗi">
                  <Tag color="orange">{data.errorAttempts}</Tag>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Trạng thái">
                <JobStatusTag status={data.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Worker">
                {data.claimedByWorkerName ?? data.claimedByWorkerId ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Khóa API">
                {data.consumerName ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Tạo lúc">
                {formatDateTime(data.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Hoàn tất lúc">
                {formatDateTime(data.completedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Thời lượng tạo">
                {formatDuration(data.createdAt, data.completedAt)}
              </Descriptions.Item>
              {data.errorMessage && (
                <Descriptions.Item label="Lỗi">
                  <ErrorText text={`[${data.errorClass}] ${data.errorMessage}`} />
                </Descriptions.Item>
              )}
            </Descriptions>

            {data.input !== undefined && (
              <>
                <Title level={5} style={{ marginTop: 16 }}>
                  Đầu vào
                </Title>
                <Paragraph style={{ marginBottom: 0 }}>
                  <pre style={preStyle}>{toJsonText(data.input)}</pre>
                </Paragraph>
              </>
            )}
          </Col>

          {/* Cột giữa: Kết quả */}
          <Col xs={24} lg={8} style={detailColStyle}>
            <Title level={5} style={{ marginTop: 0 }}>
              Kết quả
            </Title>
            {data.output !== undefined && data.output !== null ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <MediaResult output={data.output} excludeUrls={mediaUrls(data.input)} />
                </div>
                <Paragraph style={{ marginBottom: 0 }}>
                  <pre style={preStyle}>{toJsonText(data.output)}</pre>
                </Paragraph>
              </>
            ) : (
              <Text type="secondary">Chưa có kết quả.</Text>
            )}
          </Col>

          {/* Cột phải: Lần thử + Race + Sự kiện */}
          <Col xs={24} lg={8} style={detailColStyle}>
            <Title level={5} style={{ marginTop: 0 }}>
              Lần thử (Attempts)
            </Title>
            {(data.attempts ?? []).length === 0 ? (
              <Text type="secondary">Chưa có lần thử nào.</Text>
            ) : (
              <Timeline
                items={(data.attempts ?? []).map((a, i) => ({
                  color: a.errorClass ? 'red' : 'green',
                  children: (
                    <div>
                      <Text strong>Lần {a.attempt ?? i + 1}</Text> — {a.status ?? ''}
                      {a.modelName && (
                        <div>
                          <Tag color={a.errorClass ? 'red' : 'green'}>{a.modelName}</Tag>
                        </div>
                      )}
                      <div>
                        <Text type="secondary">
                          {formatDateTime(a.startedAt)} → {formatDateTime(a.finishedAt)}
                        </Text>
                      </div>
                      {a.errorMessage && (
                        <ErrorText text={`[${a.errorClass}] ${a.errorMessage}`} />
                      )}
                      <AttemptDetailButton attempt={a} index={i} />
                    </div>
                  ),
                }))}
              />
            )}

            <RaceChildrenSection jobId={data.id} raceInfo={data._race} />

            <WorkflowStepsSection jobId={data.id} info={data._workflow} />

            <Title level={5} style={{ marginTop: 16 }}>
              Sự kiện (Events)
            </Title>
            {(data.events ?? []).length === 0 ? (
              <Text type="secondary">Chưa có sự kiện nào.</Text>
            ) : (
              <Timeline
                items={(data.events ?? []).map((ev) => ({
                  children: (
                    <div>
                      <Text strong>{ev.type ?? 'event'}</Text>
                      <div>
                        <Text type="secondary">{formatDateTime(ev.createdAt)}</Text>
                      </div>
                      {ev.message &&
                        (ev.type === 'debug' ? (
                          // Giu nguyen xuong dong (curl) de copy nguyen ven hoi ben Plenxai.
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              margin: '4px 0 0',
                              padding: 8,
                              background: '#f5f5f5',
                              borderRadius: 6,
                              fontSize: 12,
                            }}
                          >
                            {ev.message}
                          </pre>
                        ) : (
                          <div>{ev.message}</div>
                        ))}
                    </div>
                  ),
                }))}
              />
            )}
          </Col>
        </Row>
      )}
    </Modal>
  );
}

/**
 * 1 cột của popup chi tiết (3 cột: thông tin+đầu vào | kết quả | lần thử+race+sự kiện).
 * Mỗi cột cuộn riêng để đọc song song, không phải kéo dài cả popup.
 */
const detailColStyle: React.CSSProperties = {
  maxHeight: '78vh',
  overflowY: 'auto',
  overflowX: 'hidden',
};

/** Độ dài tối đa của dòng lỗi rút gọn (cắt cứng để không bao giờ phá layout bảng). */
const ERROR_PREVIEW_MAX = 140;

/** Gộp lỗi nhiều dòng thành 1 dòng ngắn + "…" để xem nhanh. */
function errorPreview(text: string): string {
  const oneLine = (text ?? '').replace(/\s+/g, ' ').trim();
  return oneLine.length > ERROR_PREVIEW_MAX ? `${oneLine.slice(0, ERROR_PREVIEW_MAX)}…` : oneLine;
}

/**
 * Hiển thị lỗi gọn trên MỘT dòng (cắt chữ + "…"). Bấm vào mở popup xem toàn bộ lỗi
 * (giữ nguyên xuống dòng, có cuộn + nút sao chép). Dùng chung cho lỗi model con /
 * lần thử / lỗi tổng — tránh việc lỗi dài (stack, command) chiếm hết bảng lịch sử.
 */
function ErrorText({ text, type = 'danger' }: { text: string; type?: 'danger' | 'secondary' }) {
  const { message } = AntApp.useApp();
  const [open, setOpen] = useState(false);
  const preview = errorPreview(text);

  return (
    <>
      <Text
        type={type}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title="Bấm để xem toàn bộ lỗi"
        style={{
          display: 'block',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          textDecoration: 'underline dotted',
        }}
      >
        {preview}
      </Text>
      <Modal
        title="Chi tiết lỗi"
        open={open}
        onCancel={() => setOpen(false)}
        destroyOnClose
        centered
        width="80vw"
        style={{ maxWidth: 1100 }}
        footer={[
          <Button
            key="copy"
            onClick={() => {
              navigator.clipboard
                ?.writeText(text)
                .then(() => message.success('Đã sao chép lỗi'))
                .catch(() => message.error('Không sao chép được'));
            }}
          >
            Sao chép
          </Button>,
          <Button key="close" type="primary" onClick={() => setOpen(false)}>
            Đóng
          </Button>,
        ]}
      >
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
            padding: 12,
            maxHeight: '60vh',
            overflow: 'auto',
            background: '#f5f5f5',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          {text}
        </pre>
      </Modal>
    </>
  );
}

/**
 * Nút "Xem chi tiết" cho MỘT lần thử (model con) trong nhóm model: mở popup xem
 * request đã gửi lên nguồn đó + kết quả/lỗi thô nhận về. Trước đây chỉ xem được
 * đầu vào/kết quả của CẢ NHÓM nên không biết mỗi model con thực sự nhận gì.
 */
function AttemptDetailButton({ attempt, index }: { attempt: JobAttempt; index: number }) {
  const { message } = AntApp.useApp();
  const [open, setOpen] = useState(false);
  const hasDetail =
    attempt.requestBody != null || attempt.responseBody != null || !!attempt.requestUrl;

  const media = extractMedia(attempt.responseBody);
  const hasMedia = media.images.length + media.videos.length + media.audios.length > 0;
  const copyAll = () => {
    const text = toJsonText({
      model: attempt.modelName,
      url: attempt.requestUrl,
      request: attempt.requestBody,
      response: attempt.responseBody,
    });
    navigator.clipboard
      ?.writeText(text)
      .then(() => message.success('Đã sao chép chi tiết lần thử'))
      .catch(() => message.error('Không sao chép được'));
  };

  return (
    <>
      <Button size="small" type="link" style={{ paddingLeft: 0 }} onClick={() => setOpen(true)}>
        Xem chi tiết
      </Button>
      <Modal
        title={`Chi tiết lần ${attempt.attempt ?? index + 1}${attempt.modelName ? ` — ${attempt.modelName}` : ''}`}
        open={open}
        onCancel={() => setOpen(false)}
        destroyOnClose
        centered
        width="90vw"
        style={{ maxWidth: 1300 }}
        styles={{ body: { maxHeight: '76vh', overflow: 'auto' } }}
        footer={[
          <Button key="copy" onClick={copyAll} disabled={!hasDetail}>
            Sao chép
          </Button>,
          <Button key="close" type="primary" onClick={() => setOpen(false)}>
            Đóng
          </Button>,
        ]}
      >
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Model con">{attempt.modelName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            {attempt.status === 'success' ? <Tag color="success">Thành công</Tag> : <Tag color="error">Lỗi</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Endpoint">{attempt.requestUrl ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Thời gian">
            {formatDateTime(attempt.startedAt)} → {formatDateTime(attempt.finishedAt)}
          </Descriptions.Item>
          {attempt.errorMessage ? (
            <Descriptions.Item label="Lỗi">
              <ErrorText text={`[${attempt.errorClass}] ${attempt.errorMessage}`} />
            </Descriptions.Item>
          ) : null}
        </Descriptions>

        {!hasDetail ? (
          <Text type="secondary">
            Lần thử này chưa lưu chi tiết request/response (job tạo trước khi bật tính năng).
          </Text>
        ) : (
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Title level={5} style={{ marginTop: 0 }}>
                Yêu cầu đã gửi lên nguồn
              </Title>
              <pre style={preStyle}>{toJsonText(attempt.requestBody)}</pre>
            </Col>
            <Col xs={24} lg={12}>
              <Title level={5} style={{ marginTop: 0 }}>
                Kết quả nguồn trả về
              </Title>
              {hasMedia && (
                <div style={{ marginBottom: 12 }}>
                  <MediaResult output={attempt.responseBody} excludeUrls={mediaUrls(attempt.requestBody)} />
                </div>
              )}
              <pre style={preStyle}>{toJsonText(attempt.responseBody)}</pre>
            </Col>
          </Row>
        )}
      </Modal>
    </>
  );
}

/** Tag trạng thái cho 1 model con (race). */
function RaceChildStatusTag({ r }: { r: RaceChild }) {
  const s = r.status;
  if (s === 'completed') return <Tag color="success">Hoàn tất</Tag>;
  if (s === 'failed') return <Tag color="error">Lỗi</Tag>;
  if (s === 'cancelled')
    return <Tooltip title={r.errorMessage}><Tag color="warning">Đã hủy</Tag></Tooltip>;
  if (s === 'processing') return <Tag color="processing">Đang xử lý</Tag>;
  if (s === 'queued') return <Tag>Đang chờ</Tag>;
  return <Tag>{s}</Tag>;
}

/**
 * WORKFLOW MODEL: tien trinh tung buoc cua job cha (moi buoc = 1 job con).
 * Hien ngay duoi bang race de admin theo doi workflow chay toi dau.
 */
function WorkflowStepsSection({
  jobId,
  info,
}: {
  jobId: string;
  info?: { name?: string | null; stepCount?: number; currentNodeId?: string | null; attempt?: number };
}) {
  const has = !!info;
  const q = useWorkflowChildren(has ? jobId : undefined, has);
  if (!has) return null;
  const steps = q.data ?? [];

  return (
    <>
      <Title level={5} style={{ marginTop: 16 }}>
        Các bước workflow{info?.name ? ` · ${info.name}` : ''}
        {info?.attempt ? <Tag color="orange" style={{ marginLeft: 8 }}>Chạy lại lần {info.attempt}</Tag> : null}
      </Title>
      {q.isLoading && <Text type="secondary">Đang tải...</Text>}
      {!q.isLoading && steps.length === 0 && <Text type="secondary">Chưa chạy bước nào.</Text>}
      {steps.length > 0 && (
        <Table<WorkflowChildJob>
          dataSource={steps}
          rowKey="id"
          size="small"
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 520 }}
          columns={[
            {
              title: 'Bước (node)',
              dataIndex: 'workflowNodeId',
              width: 150,
              render: (v: string | null) => (
                <Space size={4}>
                  <span>{v || '—'}</span>
                  {v && v === info?.currentNodeId && <Tag color="processing">đang chạy</Tag>}
                </Space>
              ),
            },
            {
              title: 'Model / Nhóm',
              width: 200,
              render: (_, r) => r.groupChildModelName || r.model?.name || r.modelGroup?.name || '—',
            },
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => <JobStatusTag status={s as JobStatus} />,
            },
            {
              title: 'Lỗi',
              dataIndex: 'errorMessage',
              render: (v: string | null) => (v ? <ErrorText text={v} /> : '—'),
            },
          ]}
        />
      )}
    </>
  );
}

function RaceChildrenSection({ jobId, raceInfo }: { jobId: string; raceInfo?: { childCount?: number } }) {
  const hasRace = !!raceInfo?.childCount && raceInfo.childCount > 0;
  const raceChildren = useRaceChildren(hasRace ? jobId : undefined);

  if (!hasRace) return null;

  const children = raceChildren.data ?? [];

  // Người thắng = model con có raceWinner=true (output được dùng). Fallback cho job cũ
  // (trước khi có cờ raceWinner): model con 'completed' về đích sớm nhất.
  let winnerId: string | undefined = children.find((c) => c.raceWinner)?.id;
  if (!winnerId) {
    const completed = children
      .filter((c) => c.status === 'completed' && c.completedAt)
      .sort((a, b) => new Date(a.completedAt!).getTime() - new Date(b.completedAt!).getTime());
    winnerId = completed[0]?.id;
  }

  // Tab "Tổng quan" = bảng tóm tắt như trước (thêm cột Thời lượng tạo).
  const overviewTab = (
    <Table<RaceChild>
      dataSource={children}
      rowKey="id"
      size="small"
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 440 }}
      columns={[
        {
          title: 'Model con',
          dataIndex: 'raceMemberName',
          key: 'name',
          width: 170,
          render: (name: string, r: RaceChild) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Text strong={r.id === winnerId} style={{ wordBreak: 'break-word' }}>
                {name || r.id.slice(0, 8)}
              </Text>
              {r.id === winnerId && (
                <Tag color="success" style={{ flexShrink: 0, marginInlineEnd: 0 }}>
                  🏆 Thắng
                </Tag>
              )}
            </div>
          ),
        },
        {
          title: 'Trạng thái',
          dataIndex: 'status',
          key: 'status',
          width: 110,
          render: (_: string, r: RaceChild) => <RaceChildStatusTag r={r} />,
        },
        {
          title: 'Thời lượng',
          key: 'duration',
          width: 90,
          align: 'center' as const,
          render: (_: unknown, r: RaceChild) => <Text>{formatDuration(r.createdAt, r.completedAt)}</Text>,
        },
        {
          title: 'Lỗi / Ghi chú',
          key: 'note',
          render: (_: unknown, r: RaceChild) => {
            if (r.status === 'completed')
              return <Text type="secondary">{r.id === winnerId ? 'Về đích' : 'Hoàn tất'}</Text>;
            if (r.status === 'cancelled')
              return <Text type="secondary">{r.errorMessage || 'Bị hủy do model khác đã về đích trước'}</Text>;
            if (r.errorMessage) return <ErrorText text={r.errorMessage} />;
            return null;
          },
        },
      ]}
    />
  );

  // Mỗi model con = 1 tab riêng hiển thị lịch sử (trạng thái, thời lượng, kết quả, lỗi).
  const childTabs = children.map((c, i) => ({
    key: c.id,
    label: (
      <Space size={4}>
        <span>{c.raceMemberName || `Model ${i + 1}`}</span>
        {c.id === winnerId && <Tag color="success" style={{ marginInlineEnd: 0 }}>🏆</Tag>}
      </Space>
    ),
    children: (
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Model con">{c.raceMemberName || c.id.slice(0, 8)}</Descriptions.Item>
        <Descriptions.Item label="Trạng thái"><RaceChildStatusTag r={c} /></Descriptions.Item>
        <Descriptions.Item label="Tạo lúc">{formatDateTime(c.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Hoàn tất lúc">{formatDateTime(c.completedAt)}</Descriptions.Item>
        <Descriptions.Item label="Thời lượng tạo">{formatDuration(c.createdAt, c.completedAt)}</Descriptions.Item>
        {c.errorMessage ? (
          <Descriptions.Item label="Lỗi">
            <ErrorText text={`${c.errorClass ? `[${c.errorClass}] ` : ''}${c.errorMessage}`} />
          </Descriptions.Item>
        ) : null}
        {c.output != null ? (
          <Descriptions.Item label="Kết quả">
            <MediaResult output={c.output} />
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    ),
  }));

  return (
    <>
      <Title level={5} style={{ marginTop: 16 }}>
        🏁 Tạo tất cả model (Race) — {raceInfo.childCount} model con
      </Title>
      {raceChildren.isLoading && <Text type="secondary">Đang tải...</Text>}
      {raceChildren.data && (
        <Tabs
          size="small"
          items={[{ key: '__overview', label: 'Tổng quan', children: overviewTab }, ...childTabs]}
        />
      )}
    </>
  );
}

const preStyle: React.CSSProperties = {
  background: '#f5f5f5',
  padding: 12,
  borderRadius: 6,
  fontSize: 12,
  overflow: 'auto',
  maxHeight: 240,
};
